import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TRANSFER_CARD,
  TRANSFER_CARD_CONTENT_TABLE,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import type { PurchaseOrderReportParams } from "@/modules/reports/presentation/api/client";
import {
  usePurchaseOrderLeadTimeReportQuery,
  usePurchaseOrderReportBySupplierQuery,
} from "@/modules/reports/presentation/api/queries";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatLakDisplay, formatQtyDisplay } from "@/shared/lib/format-number";
import { cn } from "@/shared/lib/utils";
import { Loader } from "@/shared/ui/Loader";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { PO_STATUS_LABELS } from "@/modules/inventory/presentation/ui/purchase/purchaseLaoLabels";
import type { PurchaseOrderStatus } from "@/server/platform/db/schema/purchase-orders";

type Props = {
  reportParams: PurchaseOrderReportParams;
};

type ReportTab = "by-supplier" | "lead-time";

function TabCount({ n }: { n: number }) {
  return (
    <span className="ml-1.5 rounded-md bg-muted px-1.5 py-0.5 text-muted-foreground text-xs tabular-nums">
      {n.toLocaleString("lo-LA")}
    </span>
  );
}

export function PurchaseOrderReportsContent({ reportParams }: Props) {
  const [tab, setTab] = useState<ReportTab>("by-supplier");
  const bySupplier = usePurchaseOrderReportBySupplierQuery(reportParams);
  const leadTime = usePurchaseOrderLeadTimeReportQuery(reportParams);

  const supplierRows = bySupplier.data ?? [];
  const leadRows = leadTime.data?.items ?? [];

  const tabLoading =
    (tab === "by-supplier" && bySupplier.isLoading) ||
    (tab === "lead-time" && leadTime.isLoading);

  return (
    <Card className={TRANSFER_CARD}>
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as ReportTab)}
        className="gap-0"
      >
        <div className="border-border border-b px-4 pt-4 pb-0">
          <TabsList
            variant="line"
            className="h-auto w-full justify-start gap-4 bg-transparent p-0"
          >
            <TabsTrigger
              value="by-supplier"
              className="rounded-none px-1 pb-3 after:bottom-0"
            >
              ຕາມຜູ້ສະໜອງ (ຄ້າງຮັບ)
              {!bySupplier.isLoading ? (
                <TabCount n={supplierRows.length} />
              ) : null}
            </TabsTrigger>
            <TabsTrigger
              value="lead-time"
              className="rounded-none px-1 pb-3 after:bottom-0"
            >
              Lead time
              {!leadTime.isLoading ? <TabCount n={leadRows.length} /> : null}
            </TabsTrigger>
          </TabsList>
        </div>

        <CardContent className={cn(TRANSFER_CARD_CONTENT_TABLE, "pt-0")}>
          {tabLoading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : null}

          <TabsContent value="by-supplier" className="mt-0">
            {!tabLoading ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ຜູ້ສະໜອງ</TableHead>
                    <TableHead className="text-right">PO ຄ້າງ</TableHead>
                    <TableHead className="text-right">ບັນທັດ</TableHead>
                    <TableHead className="text-right">ປະລິມານຄ້າງ</TableHead>
                    <TableHead className="text-right">ມູນຄ່າຄ້າງ (₭)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supplierRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-8 text-center text-muted-foreground"
                      >
                        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງທີ່ເລືອກ
                      </TableCell>
                    </TableRow>
                  ) : (
                    supplierRows.map((row) => (
                      <TableRow key={row.supplierId}>
                        <TableCell>
                          <span className="font-medium">{row.supplierName}</span>
                          {row.supplierCode ? (
                            <span className="ml-1 text-muted-foreground text-xs">
                              {row.supplierCode}
                            </span>
                          ) : null}
                          <div className="mt-0.5">
                            <Link
                              to="/app/inventory/purchase-orders"
                              search={{
                                supplierId: row.supplierId,
                                status: "APPROVED",
                              }}
                              className="text-primary text-xs hover:underline"
                            >
                              ເບິ່ງ PO
                            </Link>
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.openPoCount.toLocaleString("lo-LA")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.openLineCount.toLocaleString("lo-LA")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatQtyDisplay(row.qtyOpen)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatLakDisplay(row.valuePendingLak)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : null}
          </TabsContent>

          <TabsContent value="lead-time" className="mt-0">
            {!tabLoading ? (
              <>
                {leadTime.data?.avgLeadDays != null ? (
                  <p className="border-border border-b px-4 py-3 text-muted-foreground text-sm">
                    PO ທີ່ຮັບແລ້ວ{" "}
                    <strong className="text-foreground tabular-nums">
                      {leadTime.data.poCountWithReceive.toLocaleString("lo-LA")}
                    </strong>{" "}
                    · ຄ່າກຸ່ມ lead time{" "}
                    <strong className="text-foreground tabular-nums">
                      {leadTime.data.avgLeadDays.toLocaleString("lo-LA")} ວັນ
                    </strong>{" "}
                    (ວັນທີສັ່ງ → ຮັບຄັ້ງທຳອິດ)
                  </p>
                ) : null}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO</TableHead>
                      <TableHead>ຜູ້ສະໜອງ</TableHead>
                      <TableHead>ວັນທີສັ່ງ</TableHead>
                      <TableHead>ຮັບຄັ້ງທຳອິດ</TableHead>
                      <TableHead className="text-right">Lead (ວັນ)</TableHead>
                      <TableHead>ສະຖານະ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leadRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="py-8 text-center text-muted-foreground"
                        >
                          ບໍ່ມີ PO ທີ່ເລີ່ມຮັບໃນຊ່ວງທີ່ເລືອກ
                        </TableCell>
                      </TableRow>
                    ) : (
                      leadRows.map((row) => (
                        <TableRow key={row.poId}>
                          <TableCell>
                            <Link
                              to="/app/inventory/purchase-orders/$id"
                              params={{ id: row.poId }}
                              className="font-mono text-primary hover:underline"
                            >
                              {row.poNo}
                            </Link>
                          </TableCell>
                          <TableCell>{row.supplierName}</TableCell>
                          <TableCell>
                            {formatDateLocal(row.orderDate)}
                          </TableCell>
                          <TableCell>
                            {row.firstReceivedAt
                              ? formatDateTimeLocal(row.firstReceivedAt)
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {row.leadDays != null
                              ? row.leadDays.toLocaleString("lo-LA")
                              : "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {PO_STATUS_LABELS[
                              row.status as PurchaseOrderStatus
                            ] ?? row.status}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </>
            ) : null}
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
}
